import { useEffect, useState } from "react";

interface WhyThisScoreProps {
  active: boolean;
  campaignId: string;
  locationId: string;
}

/**
 * AI-generated 1-2 sentence rationale for a location's FloScore. Lazily fetched
 * from the explain endpoint (server-cached). Renders nothing when there's no
 * explanation (unscored location or no AI provider configured).
 */
export function WhyThisScore({ active, campaignId, locationId }: WhyThisScoreProps) {
  const [explanation, setExplanation] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!active || !locationId) return;
    let mounted = true;
    setLoading(true);
    setExplanation(null);

    fetch(`/api/campaigns/${campaignId}/locations/${locationId}/explain`)
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (mounted) setExplanation(json?.explanation ?? null);
      })
      .catch(() => {})
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [active, campaignId, locationId]);

  if (!loading && !explanation) return null;

  return (
    <div className="mb-4">
      <p className="mb-2 font-mono text-[10.5px] uppercase tracking-[0.1em] text-muted-foreground">
        Why this score
      </p>
      {loading ? (
        <div className="space-y-1.5" aria-hidden="true">
          <div className="h-3 w-full animate-pulse rounded bg-muted" />
          <div className="h-3 w-11/12 animate-pulse rounded bg-muted" />
          <div className="h-3 w-3/4 animate-pulse rounded bg-muted" />
        </div>
      ) : (
        <p className="text-[13px] leading-relaxed text-muted-foreground">{explanation}</p>
      )}
    </div>
  );
}
